// フォームデータをRailsのnested attributes形式に変換する関数
import {
  transformOperationsAttributes,
  transformLanguagesAttributes,
  transformSimpleAttributes,
  transformDevelopmentMethodsAttributes,
  transformJoinAttributes,
  transformRolesAttributes,
} from "./transformHelpers.js";
import {
  transformBusinessSystemsAttributes,
  transformCloudServicesAttributes,
  transformServerOperatingSystemsAttributes,
  transformMiddlewaresAttributes,
} from "./complexTransformHelpers.js";

/**
 * フォームデータをRailsのnested attributes形式に変換する
 * @param {Object} formData - フォームデータ
 * @param {Object} workHistory - 既存の作業履歴（編集モード時）
 * @returns {Object} Rails形式のパラメータ
 */
export const transformToRailsParams = (formData, workHistory = null) => {
  const isEditMode = !!workHistory;

  const railsParams = {
    user: {
      work_histories_attributes: {
        0: {
          // 編集モード時のIDを設定
          ...(isEditMode && { id: workHistory.id }),

          // プロジェクト基本情報
          ...buildBasicProjectInfo(formData),

          // 開発プロセス（スクラム周期込み）
          work_histories_development_methods_attributes:
            transformDevelopmentMethodsAttributes(
              formData.development_methods,
              isEditMode
                ? workHistory?.work_histories_development_methods
                : null,
              isEditMode,
              formData.sprint_cycle_week
            ),

          // 役割
          work_histories_roles_attributes: transformRolesAttributes(
            formData.roles,
            isEditMode ? workHistory?.work_histories_roles : null,
            isEditMode,
          ),

          // 業種
          work_history_industries_attributes: transformJoinAttributes(
            formData.industry_ids,
            isEditMode ? workHistory?.work_history_industries : null,
            isEditMode,
            "industry_id",
          ),

          // 要件定義から運用/保守まで（ステップ2-7）
          ...buildOperationsAttributes(formData, workHistory, isEditMode),

          // 言語・フレームワーク（ステップ8-9）
          work_histories_languages_attributes: transformLanguagesAttributes(
            formData.frontend_languages,
            formData.backend_languages,
            isEditMode ? workHistory?.work_histories_languages : null,
            isEditMode
          ),

          // 業務システム・クラウド・サーバーOS（ステップ10-12）
          work_histories_business_systems_attributes:
            transformBusinessSystemsAttributes(
              formData.business_systems,
              isEditMode ? workHistory?.work_histories_business_systems : null,
              isEditMode
            ),

          work_histories_cloud_services_attributes:
            transformCloudServicesAttributes(
              formData.cloud_services,
              isEditMode ? workHistory?.work_histories_cloud_services : null,
              isEditMode
            ),

          work_histories_server_operating_systems_attributes:
            transformServerOperatingSystemsAttributes(
              formData.server_operating_systems,
              isEditMode
                ? workHistory?.work_histories_server_operating_systems
                : null,
              isEditMode
            ),

          // ステップ13: MW
          work_histories_middlewares_attributes: transformMiddlewaresAttributes(
            formData.middlewares,
            workHistory?.work_histories_middlewares,
            isEditMode
          ),

          // その他のツール・技術（ステップ13-18）
          ...buildSimpleToolsAttributes(formData, workHistory, isEditMode),
        },
      },
    },
  };
  return railsParams;
};

/**
 * ステップ1のプロジェクト基本情報を構築
 * @param {Object} formData - フォームデータ
 * @returns {Object} 基本情報オブジェクト
 */
const buildBasicProjectInfo = (formData) => ({
  name: formData.name || "",
  period_from: formData.period_from || "",
  period_at: formData.participation_flag ? "" : formData.period_at,
  participation_flag: formData.participation_flag ? "1" : "0",
  project_scale: formData.project_scale || "",
  team_scale: formData.team_scale || "",
  achievement: formData.achievement || "",
});

/**
 * ステップ2-7の操作系の属性を構築（要件定義〜運用保守）
 * @param {Object} formData - フォームデータ
 * @param {Object} workHistory - 既存の作業履歴
 * @param {boolean} isEditMode - 編集モードかどうか
 * @returns {Object} 操作系属性オブジェクト
 */
const buildOperationsAttributes = (formData, workHistory, isEditMode) => ({
  work_history_requirements_definition_operations_attributes:
    transformOperationsAttributes(
      formData.requirements_definition_operations,
      isEditMode
        ? workHistory?.work_history_requirements_definition_operations
        : null,
      "requirements_definition_operation_id",
      isEditMode
    ),

  work_history_basic_design_operations_attributes:
    transformOperationsAttributes(
      formData.basic_design_operations,
      isEditMode ? workHistory?.work_history_basic_design_operations : null,
      "basic_design_operation_id",
      isEditMode
    ),

  work_history_detail_design_operations_attributes:
    transformOperationsAttributes(
      formData.detail_design_operations,
      isEditMode ? workHistory?.work_history_detail_design_operations : null,
      "detail_design_operation_id",
      isEditMode
    ),

  work_history_program_operations_attributes: transformOperationsAttributes(
    formData.program_operations,
    isEditMode ? workHistory?.work_history_program_operations : null,
    "program_operation_id",
    isEditMode
  ),

  work_history_test_operations_attributes: transformOperationsAttributes(
    formData.test_operations,
    isEditMode ? workHistory?.work_history_test_operations : null,
    "test_operation_id",
    isEditMode
  ),

  work_history_maintenance_operation_operations_attributes:
    transformOperationsAttributes(
      formData.maintenance_operations,
      isEditMode
        ? workHistory?.work_history_maintenance_operation_operations
        : null,
      "maintenance_operation_operation_id",
      isEditMode
    ),
});

/**
 * ステップ13-18のシンプルなツール系の属性を構築
 * @param {Object} formData - フォームデータ
 * @param {Object} workHistory - 既存の作業履歴
 * @param {boolean} isEditMode - 編集モードかどうか
 * @returns {Object} ツール系属性オブジェクト
 */
const buildSimpleToolsAttributes = (formData, workHistory, isEditMode) => ({
  work_histories_operating_systems_attributes: transformSimpleAttributes(
    formData.operating_systems,
    isEditMode ? workHistory?.work_histories_operating_systems : null,
    "operating_system",
    isEditMode
  ),

  work_histories_dbs_attributes: transformSimpleAttributes(
    formData.dbs,
    isEditMode ? workHistory?.work_histories_dbs : null,
    "db",
    isEditMode
  ),

  work_histories_networks_attributes: transformSimpleAttributes(
    formData.networks,
    isEditMode ? workHistory?.work_histories_networks : null,
    "network",
    isEditMode
  ),

  work_histories_design_tools_attributes: transformSimpleAttributes(
    formData.design_tools,
    isEditMode ? workHistory?.work_histories_design_tools : null,
    "design_tool",
    isEditMode
  ),

  work_histories_tools_attributes: transformSimpleAttributes(
    formData.tools,
    isEditMode ? workHistory?.work_histories_tools : null,
    "tool",
    isEditMode
  ),
});
